import { useNavigate } from 'react-router-dom';
import { setLoggedIn } from '@/components/auth/ProtectedRoute';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, Bell, LogOut, Settings, User, CheckCheck, Compass } from 'lucide-react';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { useTour } from '@/components/tour/TourProvider';
import { useSimulation } from '@/hooks/useSimulation';
import { currentUser } from '@/data/mockData';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { t } from '@/i18n';

function relativeTime(ts) {
  if (!ts) return '';
  const diff = Math.round((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return t('header.justNow');
  if (diff < 3600) return `${Math.floor(diff / 60)} ${t('header.minAgo')}`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ${t('header.hrAgo')}`;
  return `${Math.floor(diff / 86400)}${t('header.dAgo')}`;
}

export const Header = ({ title, onOpenSearch }) => {
  const { startTour } = useTour();
  const navigate = useNavigate();
  const { dashboard } = useSimulation();
  const activeAlerts = dashboard.alerts.filter(a => !a.dismissed);
  const alertCount = activeAlerts.length;

  return (
    <div className="fixed top-0 lg:left-60 left-0 right-0 h-16 bg-card border-b border-border flex items-center justify-between px-4 lg:px-8 z-10">
      <div className="flex items-center gap-3 min-w-0">
        <h1 className="text-xl font-bold tracking-tight truncate">{title}</h1>
      </div>
      {/* Tighter on phones so every control fits in 320 px, in every language */}
      <div className="flex items-center gap-1 sm:gap-3 shrink-0 ml-2">
        {/* Search button */}
        <Button variant="outline" size="sm" className="gap-2 text-muted-foreground px-2 sm:px-3" onClick={onOpenSearch} aria-label={t('header.search')}>
          <Search className="h-3.5 w-3.5" />
          <span className="text-xs hidden lg:inline">{t('header.search')}</span>
          <kbd className="ml-1 text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono hidden lg:inline">⌘K</kbd>
        </Button>

        {/* Notifications */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative h-8 w-8">
              <Bell className="h-4 w-4" />
              {alertCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {alertCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="end">
            <div className="p-3 border-b flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">{t('header.notifications')}</div>
                <div className="text-xs text-muted-foreground">{alertCount} {t('header.activeAlerts')}</div>
              </div>
              {alertCount > 0 && (
                <Button variant="ghost" size="sm" className="text-xs gap-1 h-7 text-muted-foreground" onClick={() => toast.success(t('header.markAllReadToast'))}>
                  <CheckCheck className="h-3 w-3" /> {t('header.markAllRead')}
                </Button>
              )}
            </div>
            <div className="max-h-64 overflow-y-auto">
              {activeAlerts.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground text-center">{t('header.allClear')}</div>
              ) : (
                activeAlerts.map(alert => (
                  <div key={alert.id} className="flex items-start gap-2 px-3 py-2.5 border-b last:border-0 hover:bg-muted/50">
                    <div className={cn('w-2 h-2 rounded-full mt-1.5 shrink-0',
                      alert.type === 'error' && 'bg-red-500',
                      alert.type === 'warning' && 'bg-yellow-500',
                      alert.type === 'success' && 'bg-green-500',
                      alert.type === 'info' && 'bg-blue-500',
                    )} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">{t(alert.message)}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {relativeTime(alert.timestamp)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* On phones only the pulsing dot stays; the label is in the tooltip */}
        <Badge variant="secondary" className="font-normal px-1.5 sm:px-2.5" title={t('header.live')} aria-label={t('header.live')}>
          <span className="w-2 h-2 bg-green-500 rounded-full sm:mr-2 status-pulse-green"></span>
          <span className="hidden sm:inline">{t('header.live')}</span>
        </Badge>

        {/* Guided tour, can be replayed any time */}
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 h-8 px-2"
          onClick={startTour}
          aria-label={t('tour.open')}
          title={t('tour.open')}
          data-tour="tour-button"
        >
          <Compass className="h-3.5 w-3.5" />
          <span className="hidden md:inline text-xs font-medium">{t('tour.open')}</span>
        </Button>

        {/* Language switcher */}
        <LanguageSwitcher />

        {/* Profile dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0">
              <Avatar className="h-8 w-8 cursor-pointer">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {currentUser.initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{currentUser.name}</p>
                <p className="text-xs text-muted-foreground">{currentUser.role}</p>
                <p className="text-xs text-muted-foreground">{currentUser.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <User className="mr-2 h-4 w-4" />
              <span>{t('header.profile')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              <Settings className="mr-2 h-4 w-4" />
              <span>{t('header.settings')}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { setLoggedIn(false); navigate('/'); }}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>{t('header.signOut')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
